import { cn } from '@rocco/utils/cn';
import { Play } from 'lucide-react';
import { useState } from 'react';

import { Image } from '@rocco/components/image';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { DetailImageOverlay } from '~/ui-rocco/pages/detail-shared';
import type { OverlaySlide } from '~/ui-rocco/pages/detail-shared';
import { TileActionPanel } from '~/modules/moodboards/components/TileActionPanel';

/** Five across the content column, so each is asked for at about a fifth. */
const GRID_SIZES = getImagesSizes({ desktop: 380, mobile: 414 });

/**
 * Every photograph in the project, as a plain grid.
 *
 * The alternative to the gallery: the page-builder modules are an edit — sized,
 * ordered and interleaved with copy — and this is the contact sheet. Same
 * pictures, none of the composition, and a page a reader can get to the bottom
 * of. Clicking one opens the viewer at that image, which then pages through the
 * whole set.
 *
 * Square crops on purpose. The modules carry the real proportions; a grid whose
 * job is to let you find a picture reads better on one rhythm.
 */
export const ProjectImageGrid = ({
    slides,
    projectId,
    title,
    professional,
}: {
    slides: OverlaySlide[];
    projectId: number;
    title: string;
    /**
     * Whose project this is, for the hover panel. Omit and the pictures keep
     * their click-to-open and lose only the panel, which is what a surface
     * without the professional to hand should get.
     */
    professional?: {
        id: number;
        title: string;
        link: string;
        website?: string | null;
    };
}) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    if (slides.length === 0) return null;

    return (
        <>
            <div className="grid grid-cols-2 max-md:gap-2 md:grid-cols-4 md:gap-3">
                {slides.map((slide, index) => (
                    // The panel is a sibling of the button, not a child: its
                    // pills are controls, and a control inside a button is
                    // neither valid nor reachable. `group` moves up here so
                    // both still answer to the same hover.
                    <div
                        key={`${slide.src}-${index}`}
                        className="group relative aspect-square w-full overflow-hidden rounded-tile bg-white/5"
                    >
                        <button
                            type="button"
                            onClick={() => setOpenIndex(index)}
                            aria-label={`View ${slide.alt || title}`}
                            className="absolute inset-0 cursor-pointer p-0"
                        >
                            <Image
                                src={slide.src}
                                alt={slide.alt}
                                fill
                                fit="cover"
                                sizes={GRID_SIZES}
                                fadeIn={false}
                                className={cn(
                                    'transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transition-none',
                                    // A film's poster is greyed the way the
                                    // gallery greys its own, so the same
                                    // picture reads as a film in both places —
                                    // its poster is often one of the project's
                                    // photographs, and in colour the two tiles
                                    // were indistinguishable but for the mark.
                                    slide.video && 'grayscale-[0.8]',
                                )}
                            />
                            {/* A film's tile is its poster, which is a still —
                                the mark is what says it will play. Inside the
                                button, so the whole tile opens it. */}
                            {slide.video ? (
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                    <span className="relative flex size-[3.25rem] items-center justify-center rounded-full bg-black/40 transition-colors group-hover:bg-black/55">
                                        <Play
                                            className="size-[1.375rem] fill-white text-white"
                                            strokeWidth={0}
                                        />
                                    </span>
                                </span>
                            ) : null}
                        </button>
                        {professional ? (
                            <div className="pointer-events-none absolute inset-0">
                                <TileActionPanel
                                    // Saves this photograph, not the project
                                    // the contact sheet belongs to — the
                                    // project has its own Save in the CTA
                                    // cluster, and pinning every tile to it
                                    // made one save read as "Saved" on all
                                    // fifteen pictures. A picture the walk
                                    // found no library image behind can only
                                    // be saved as the project, so it says so.
                                    pinId={slide.id ?? projectId}
                                    libraryItemId={projectId}
                                    pinType={
                                        slide.id ? 'PhotoPin' : 'ProjectPin'
                                    }
                                    enquiry={{
                                        itemType: 'project',
                                        siteTreeID: projectId,
                                        professionalId: professional.id,
                                        professionalName: professional.title,
                                        professionalLink: professional.link,
                                        itemTitle: title,
                                    }}
                                    visitWebsite={
                                        professional.website
                                            ? {
                                                  website: professional.website,
                                                  libraryItemID: projectId,
                                                  professionalID:
                                                      professional.id,
                                                  professionalTitle:
                                                      professional.title,
                                              }
                                            : null
                                    }
                                    share={{ url: '', title }}
                                    tileInfo={{
                                        title,
                                        subtitle: professional.title,
                                        imageShape: 'square',
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>

            {openIndex !== null ? (
                <DetailImageOverlay
                    slides={slides}
                    index={openIndex}
                    onIndexChange={setOpenIndex}
                    onClose={() => setOpenIndex(null)}
                    pinId={projectId}
                    pinType="ProjectPin"
                    title={title}
                />
            ) : null}
        </>
    );
};
